import { Dialog, Grid, pxToRem, useStyles } from '@archipro-design/aria';
import { CloseLine } from '@archipro-design/icons';
import type {
    LibraryDiagram,
    LibraryImage,
    LibraryVideo,
} from '@modules/product/type/product-gallery-types';
import { isLibraryVideo } from '@modules/product/util/is-library-video';

import ImageSlide from './ImageSlide';
import * as S from './PhotoViewerModal.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import React from 'react';
import { useInViewport } from 'ahooks';
import { decodeEntities } from '~/utils';

interface PhotoViewerModalProps {
    stack: Array<LibraryImage | LibraryVideo | LibraryDiagram>;
    selectedSlide: number;
    open: boolean;
    toggle: () => void;
    itemID?: number;
    title?: string;
}

const PhotoViewerModal = ({
    stack,
    selectedSlide,
    open,
    toggle,
    title,
}: PhotoViewerModalProps) => {
    const { css } = useStyles();

    const containerRef = React.useRef(null);
    const contentRef = React.useRef<(HTMLDivElement | null)[]>([]);

    const [inViewport] = useInViewport(containerRef, {
        threshold: 0,
        rootMargin: '100%',
    });

    const slides = stack.flatMap((slide, idx) => {
        if (isLibraryVideo(slide)) return [];

        const libraryImageSrc =
            slide.__typename === 'LibraryImage' ||
            slide.__typename === 'DiagramImage'
                ? slide.LibraryImageFile?.ThumbCategory ?? ''
                : '';
        return {
            key: `${slide.ID}-ImageSlide`,
            id: slide.ID,
            content: (
                <ImageSlide
                    key={slide.ID}
                    libraryImage={libraryImageSrc}
                    height={1}
                    width={slide.AspectRatio}
                    isMultiplied={slide.IsMultiplied}
                    containerHeight={780}
                    containerWidth={1768}
                    sizes={getImagesSizes(1768)}
                    loadingValue="eager"
                    objectFit="contain"
                    objectPosition="center"
                    ref={(ref) => (contentRef.current[idx] = ref)}
                    caption={decodeEntities(slide.Caption ?? '')}
                    alt={slide.AltText || `${title} gallery detail image`}
                />
            ),
        };
    });

    React.useEffect(() => {
        if (!inViewport) return;

        const element = contentRef.current[selectedSlide];
        if (element && selectedSlide > 0) {
            element.scrollIntoView({
                behavior: 'smooth',
            });
        }
    }, [inViewport, selectedSlide]);

    return (
        <Dialog
            ref={containerRef}
            open={open}
            onCancel={() => toggle()}
            headerAction={{
                icon: (
                    <CloseLine
                        className={css({
                            width: `${pxToRem(40)} !important`,
                            height: `${pxToRem(40)} !important`,
                        })}
                    />
                ),
                title: 'Close',
                onClick: () => toggle(),
            }}
            content={{
                content: (
                    <Grid columns={1} className={css(S.StackContainer)}>
                        {slides.map((s) => s.content)}
                    </Grid>
                ),
            }}
            variables={{
                rootWidth: pxToRem(1920),
                rootMaxHeight: '100vh',
                overflow: true,
            }}
            className={css(S.PhotoViewerContainer)}
        />
    );
};

export default PhotoViewerModal;
